import type { AssetManifestKey } from "../../assets/manifest";
import type { ProfileProgress } from "../progression/levels";
import type { OrbitForgeSave } from "../save/types";
import {
  cosmeticDefinitionsFor,
  cosmeticFeedbackTarget,
  isCosmeticUnlocked,
  type CosmeticDefinition,
  type CosmeticKind,
  type CosmeticSource
} from "./catalog";

export interface CosmeticItemView {
  readonly kind: CosmeticKind;
  readonly id: string;
  readonly label: string;
  readonly source: CosmeticSource;
  readonly unlocked: boolean;
  readonly selected: boolean;
  readonly stateLabel: string;
  readonly unlockLabel: string;
  readonly assetKey: AssetManifestKey | null;
  readonly feedbackTarget: string;
}

export interface CosmeticPanelView {
  readonly shellSkins: readonly CosmeticItemView[];
  readonly corePatterns: readonly CosmeticItemView[];
  readonly selectedShellSkinLabel: string;
  readonly selectedCorePatternLabel: string;
  readonly unlockedCount: number;
  readonly totalCount: number;
}

export function buildCosmeticPanelView(save: OrbitForgeSave, progress: ProfileProgress): CosmeticPanelView {
  const shellSkins = buildCosmeticItems("shellSkin", save, progress);
  const corePatterns = buildCosmeticItems("corePattern", save, progress);
  const allItems = [...shellSkins, ...corePatterns];

  return {
    shellSkins,
    corePatterns,
    selectedShellSkinLabel: shellSkins.find((item) => item.selected)?.label ?? "White Arc",
    selectedCorePatternLabel: corePatterns.find((item) => item.selected)?.label ?? "Clean Core",
    unlockedCount: allItems.filter((item) => item.unlocked).length,
    totalCount: allItems.length
  };
}

function buildCosmeticItems(kind: CosmeticKind, save: OrbitForgeSave, progress: ProfileProgress): readonly CosmeticItemView[] {
  return cosmeticDefinitionsFor(kind).map((definition) => buildCosmeticItem(definition, save, progress));
}

function buildCosmeticItem(definition: CosmeticDefinition, save: OrbitForgeSave, progress: ProfileProgress): CosmeticItemView {
  const unlocked = isCosmeticUnlocked(definition, save, progress);
  const selectedId = definition.kind === "shellSkin" ? save.cosmetics.SelectedShellSkin : save.cosmetics.SelectedCorePattern;
  const selected = unlocked && selectedId === definition.id;

  return {
    kind: definition.kind,
    id: definition.id,
    label: definition.label,
    source: definition.source,
    unlocked,
    selected,
    stateLabel: selected ? "EQUIPPED" : unlocked ? "EQUIP" : lockedLabel(definition),
    unlockLabel: unlocked ? sourceLabel(definition.source) : lockedLabel(definition),
    assetKey: definition.assetKey,
    feedbackTarget: cosmeticFeedbackTarget(definition.kind, definition.id)
  };
}

function lockedLabel(definition: CosmeticDefinition): string {
  if (definition.source === "level") {
    return `REQ LVL ${definition.unlockLevel ?? 1}`;
  }
  if (definition.source === "prestige") {
    return "PRESTIGE";
  }
  return "SUPPLY CRATE";
}

function sourceLabel(source: CosmeticSource): string {
  if (source === "crate") {
    return "CRATE";
  }
  if (source === "prestige") {
    return "PRESTIGE";
  }
  return "LEVEL";
}
